
import React, { useMemo } from "react";
import { Card, Col, Container, Form, Row, Table } from "react-bootstrap";
import Loader from "../../Loader/Loader";
import { commonService } from "../../../utils/commonService";

const LEVEL_COLORS = {
  "Very High": "#1b9e4b",
  High: "#6dad2e",
  Moderate: "#e5a819",
  Low: "#e07416",
  "Very Low": "#d32f2f",
};

const getLevelLabel = (score) => {
  if (score >= 85) return "Very High";
  if (score >= 70) return "High";
  if (score >= 55) return "Moderate";
  if (score >= 40) return "Low";
  return "Very Low";
};

const getLevelColor = (level) => {
  return LEVEL_COLORS[level] || "#6C757D";
};

const Cancellation = ({ data, onUpdate, demandScoreData, loading }) => {
  const method = data?.method || "blended";
  const beta = Number(data?.beta ?? 0.3);

  const previewRows = useMemo(() => {
    if (
      !demandScoreData ||
      !Array.isArray(demandScoreData) ||
      demandScoreData.length === 0
    ) {
      return [];
    }

    return demandScoreData.map((item) => {
      const demandScore = Number(item.demandscore || item.demandScore || 0);
      const cf = Number(item.cancellation_score || item.CF || 0);

      let adjCF = cf;
      let final = demandScore;

      if (method === "blended") {
        adjCF = 1 - beta * (cf);
        final = demandScore * adjCF;
      } else if (method === "multiply") {
        final = demandScore * cf;
      }

      const drop = final - demandScore;
      const riskPercent = cf * 100;

      return {
        date: item.StayDate || item.stayDate || item.date || "—",
        cf,
        risk: riskPercent,
        adjCF,
        final,
        drop,
        level: getLevelLabel(final),
      };
    });
  }, [demandScoreData, method, beta]);

  const colSpanValue = method === "blended" ? 7 : 6;

  return (
    <div className="cancellation-container mt-3">
      <Container fluid>
        {/* Method selection */}
        <Row>
          <Col lg={12}>
            <div className="info-section mb-0">
              <h6 className="info-title">Cancellation Adjustment Method</h6>
              <p className="info-text">
                Controls how cancellation risk modifies the raw Demand Score.{" "}
                <strong>Blended method</strong> passes only beta fraction of
                risk.
              </p>
            </div>
          </Col>
        </Row>

        <Row className="mt-1 g-3 mb-3">
          <Col md={6}>
            <button id={commonService.generateGlobalClickTrackerID("Cancellation", "blended-recommended-final-demand-1", data)}
              type="button"
              className={`booking-metric-card ${method === "blended" ? "active" : ""}`}
              onClick={() => onUpdate?.("method", "blended")}
              style={{ width: "100%", textAlign: "left" }}
            >
              <div className="metric-title">Blended (Recommended)</div>
              <div className="metric-description">
                Final = Demand × (1 − β × (CF))
              </div>
            </button>
          </Col>
          <Col md={6}>
            <button id={commonService.generateGlobalClickTrackerID("Cancellation", "direct-multiply-legacy-final-demand", data)}
              type="button"
              className={`booking-metric-card ${method === "multiply" ? "active" : ""}`}
              onClick={() => onUpdate?.("method", "multiply")}
              style={{ width: "100%", textAlign: "left" }}
            >
              <div className="metric-title">Direct Multiply (Legacy)</div>
              <div className="metric-description">Final = Demand × CF</div>
            </button>
          </Col>
        </Row>

        {method === "blended" && (
          <Row className="mb-3">
            <Col lg={12}>
              <Card className="beta-cancellation-card">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>beta (Sensitivity)</strong>
                    <span className="ds-range-value">{beta.toFixed(2)}</span>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <small className="text-muted ds-range-bound">0.05</small>
                    <Form.Range id={commonService.generateGlobalClickTrackerID("Cancellation", "form-range-onchange", data)}
                      className="ds-range-input"
                      min={0.05}
                      max={1}
                      step={0.01}
                      value={beta}
                      onChange={(e) =>
                        onUpdate?.("beta", Number(e.target.value))
                      }
                    />
                    <small className="text-muted ds-range-bound">1.00</small>
                  </div>
                  <div className="mt-2 text-muted" style={{ fontSize: "12px" }}>
                    <strong style={{ color: "#ff9800" }}>
                      Interpretation:
                    </strong>{" "}
                    beta={beta.toFixed(2)} means only {(beta * 100).toFixed(0)}%
                    of cancel risk passes through.
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        <Row className="mt-2">
          <Col lg={12}>
            <h6 className="section-title" style={{ marginBottom: "15px" }}>
              Impact Preview
            </h6>
          </Col>
        </Row>

        <Row>
          <Col lg={12}>
            <div className="ViewPropertySetupTable mb-3">
              <div className="stripped__table">
                <div className="table-responsive tblDynamicPickupSticky">
                  <Table
                    className="table-with-spacing positionStaticTable cancellationTbl"
                    style={{ fontSize: "10px" }}
                  >
                    <thead className="tbl-header-sticky">
                      <tr
                        style={{
                          background: "#1a1f2e",
                          borderBottom: "2px solid rgba(255, 255, 255, 0.1)",
                        }}
                      >
                        <th className="">DATE</th>
                        <th className="text-center">CF</th>
                        <th className="text-center">RISK %</th>
                        {method === "blended" && (
                          <th className="text-center">ADJ CF</th>
                        )}
                        <th className="text-center">FINAL</th>
                        <th className="text-center">DROP</th>
                        <th className="text-center border-right">LEVEL</th>
                      </tr>
                    </thead>

                    <tbody>
                      {loading ? (
                        <tr style={{ height: "80px", position: "relative" }}>
                          <Loader />
                        </tr>
                      ) : previewRows.length === 0 ? (
                        <tr>
                          <td
                            colSpan={colSpanValue}
                            className="text-center py-4"
                            style={{ color: "#888", fontSize: "13px" }}
                          >
                            No demand score data available for selected period
                          </td>
                        </tr>
                      ) : (
                        previewRows.map((row, index) => {
                          const isHighRisk = row.risk >= 15;
                          const isSignificantDrop = row.drop <= -3;

                          return (
                            <tr
                              key={index}
                              style={{
                                borderBottom:
                                  "1px solid rgba(255,255,255,0.08)",
                              }}
                            >
                              <td className="">
                                {commonService.getDateInFormat(row.date)}
                              </td>
                              <td
                                className="text-center"
                                style={{ fontWeight: 600 }}
                              >
                                {row.cf.toFixed(2)}
                              </td>
                              <td
                                className="text-center"
                                style={{
                                  fontWeight: isHighRisk ? 700 : 500,
                                  color: isHighRisk ? "#d32f2f" : "#e07416",
                                }}
                              >
                                {row.risk.toFixed(0)}%
                              </td>
                              {method === "blended" && (
                                <td
                                  className="text-center"
                                  style={{
                                    fontWeight: 600,
                                    color: "#6dad2e",
                                  }}
                                >
                                  {row.adjCF.toFixed(3)}
                                </td>
                              )}
                              <td
                                className="text-center"
                                style={{
                                  fontWeight: 700,
                                  color: "#FF9800",
                                  background: "rgba(255,152,0,0.06)",
                                }}
                              >
                                {row.final.toFixed(2)}
                              </td>
                              <td
                                className="text-center"
                                style={{
                                  fontWeight: isSignificantDrop ? 700 : 500,
                                  color: isSignificantDrop
                                    ? "#d32f2f"
                                    : "#e07416",
                                }}
                              >
                                {row.drop >= 0 ? "+" : ""}
                                {row.drop.toFixed(2)}
                              </td>
                              <td className="text-center border-right">
                                <span
                                  style={{
                                    padding: "2px 6px",
                                    borderRadius: "4px",
                                    background: getLevelColor(row.level),
                                    color: "white",
                                    fontSize: "9px",
                                    fontWeight: 700,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {row.level}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Cancellation;